import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Drawer, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { RootState } from '../../../redux/store';
import { baseName } from '../media/MediaCard';
import { usePageHeader } from '../../../context/PageHeaderContext';
import axios from '../../../interceptors/axios';
import { cardSx, glassSx } from '../../../theme/sx';
import { BarChart, HourChart, chartColor } from './Charts';
import CalendarPicker from './CalendarPicker';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FileRow {
  file_id: number; name: string; plays: number; plays_error: number;
  duration_ms: string; players: number; first_at: string; last_at: string;
}
interface PlayerRow {
  device_id: string; name: string | null; last_seen_at: string | null;
  plays: number; duration_ms: string; files: number;
  first_at: string; last_at: string;
}
interface ReportData {
  period: { from: string; to: string; granularity: string; timezone: string };
  summary: {
    total_plays: number; total_errors: number; total_duration_ms: string;
    files_selected: number; players_with_plays: number;
    players_total: number; players_active: number;
  };
  series: { bucket: string; plays: number }[];
  by_hour: number[];
  by_file: FileRow[];
  by_player: PlayerRow[];
}

type Mode   = 'files' | 'players';
type Period = 'day' | 'week' | 'month' | 'custom';

// ── Helpers ───────────────────────────────────────────────────────────────────

const iso = (d: Date) => d.toISOString().slice(0, 10);

function setToArray<T>(set: Set<T>): T[] {
  const out: T[] = [];
  set.forEach(v => out.push(v));
  return out;
}

function periodRange(p: Period): { from: string; to: string } {
  const today = new Date();
  const back  = (days: number) => iso(new Date(Date.now() - days * 86400000));
  if (p === 'day')  return { from: iso(today), to: iso(today) };
  if (p === 'week') return { from: back(6),    to: iso(today) };
  return { from: back(29), to: iso(today) };
}

const fmtHours = (ms: number) => {
  const h = Math.floor(ms / 3600000);
  const m = Math.round((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtDur = (ms: string | number) => {
  const s = Math.round(Number(ms) / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ` +
         `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const ONLINE_MS = 6 * 60 * 1000;
const isOnline  = (last: string | null) => !!last && Date.now() - new Date(last).getTime() < ONLINE_MS;

// ── Component ─────────────────────────────────────────────────────────────────

const ReportPage: React.FC = () => {
  const { t }  = useTranslation();
  const auth   = useSelector((s: RootState) => s.auth.value);
  const { setBreadcrumbs } = usePageHeader();

  const [period, setPeriod] = useState<Period>('day');
  const [range,  setRange]  = useState(periodRange('day'));
  const [mode,   setMode]   = useState<Mode>('players');
  const [search, setSearch] = useState('');

  const [catalog, setCatalog] = useState<ReportData | null>(null);
  const [detail,  setDetail]  = useState<ReportData | null>(null);
  const [selFiles,   setSelFiles]   = useState<Set<number>>(new Set());
  const [selPlayers, setSelPlayers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Active item — single-click to open detail view on the right
  const [activeId,     setActiveId]     = useState<string | null>(null);
  const [activeDetail, setActiveDetail] = useState<ReportData | null>(null);
  const [activeLoading, setActiveLoading] = useState(false);

  // Mobile bottom sheets
  const isMobile = useMediaQuery('(max-width:900px)');
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [mobileAggOpen,    setMobileAggOpen]    = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ label: t('reports.title') }]);
    return () => setBreadcrumbs([]);
  }, [t, setBreadcrumbs]);

  // Reset active detail whenever period or mode changes
  useEffect(() => { setActiveId(null); setActiveDetail(null); }, [mode, range.from, range.to]);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setActiveId(null);
    setActiveDetail(null);
    try {
      const { data } = await axios.get<ReportData>('/reports/data', {
        params: { from: range.from, to: range.to, granularity: 'day' },
      });
      setCatalog(data);
      setSelFiles(new Set(data.by_file.map(f => f.file_id)));
      setSelPlayers(new Set(data.by_player.map(p => p.device_id)));
    } catch {}
    finally { setLoading(false); }
  }, [range.from, range.to]);

  useEffect(() => { if (auth) fetchCatalog(); }, [auth, fetchCatalog]);

  const fetchDetail = useCallback(async () => {
    if (!catalog) return;
    const params: Record<string, string> = { from: range.from, to: range.to, granularity: 'day' };
    if (mode === 'files'   && selFiles.size   && selFiles.size   !== catalog.by_file.length)
      params.file_ids   = setToArray(selFiles).join(',');
    if (mode === 'players' && selPlayers.size && selPlayers.size !== catalog.by_player.length)
      params.device_ids = setToArray(selPlayers).join(',');
    try {
      const { data } = await axios.get<ReportData>('/reports/data', { params });
      setDetail(data);
    } catch {}
  }, [catalog, range.from, range.to, mode, selFiles, selPlayers]);

  useEffect(() => { if (auth && catalog) fetchDetail(); }, [auth, catalog, fetchDetail]);

  // Open single-item detail (right zone click)
  const openDetail = useCallback(async (id: string) => {
    setActiveId(id);
    setActiveLoading(true);
    try {
      const params: Record<string, string> = { from: range.from, to: range.to, granularity: 'day' };
      if (mode === 'players') params.device_ids = id;
      else                    params.file_ids   = id;
      const { data } = await axios.get<ReportData>('/reports/data', { params });
      setActiveDetail(data);
    } catch {}
    finally { setActiveLoading(false); }
  }, [range.from, range.to, mode]);

  const applyPeriod = (p: Period) => {
    setPeriod(p);
    if (p !== 'custom') setRange(periodRange(p));
  };

  // ── Left list rows ───────────────────────────────────────────────────────────

  const rows = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    return mode === 'files'
      ? catalog.by_file
          .filter(f => !q || f.name.toLowerCase().includes(q))
          .map(f => ({ id: String(f.file_id), name: baseName(f.name), value: f.plays, meta: `${f.players} ${t('reports.playersWord')}` }))
      : catalog.by_player
          .filter(p => !q || (p.name || p.device_id).toLowerCase().includes(q))
          .map(p => ({ id: p.device_id, name: p.name || p.device_id, value: p.plays, meta: `${p.files} ${t('reports.filesWord')}` }));
  }, [catalog, mode, search]);

  const isSelected = (id: string) =>
    mode === 'files' ? selFiles.has(Number(id)) : selPlayers.has(id);

  const toggleRow = (id: string) => {
    if (mode === 'files') {
      const next = new Set(selFiles);
      next.has(Number(id)) ? next.delete(Number(id)) : next.add(Number(id));
      setSelFiles(next);
    } else {
      const next = new Set(selPlayers);
      next.has(id) ? next.delete(id) : next.add(id);
      setSelPlayers(next);
    }
  };

  const toggleAll = () => {
    const allSelected = rows.every(r => isSelected(r.id));
    if (mode === 'files')
      setSelFiles(allSelected ? new Set() : new Set(rows.map(r => Number(r.id))));
    else
      setSelPlayers(allSelected ? new Set() : new Set(rows.map(r => r.id)));
  };

  const selectedCount = rows.filter(r => isSelected(r.id)).length;
  const selectedSum   = rows.filter(r => isSelected(r.id)).reduce((s, r) => s + r.value, 0);
  const maxValue      = Math.max(...rows.map(r => r.value), 1);

  // ── Right panel ──────────────────────────────────────────────────────────────

  const d          = detail;
  const airtimeMs  = Number(d?.summary.total_duration_ms ?? 0);

  const rightTitle = activeId
    ? rows.find(r => r.id === activeId)?.name ?? activeId
    : selectedCount === 0
      ? t('reports.nothingSelected')
      : selectedCount === 1
        ? rows.find(r => isSelected(r.id))?.name ?? ''
        : `${selectedCount} ${mode === 'files' ? t('reports.filesWord') : t('reports.playersWord')} · ${t('reports.summed')}`;

  // ── Render ───────────────────────────────────────────────────────────────────

  // ── Shared list rows (used in both mobile and desktop) ──────────────────────

  const listRows = rows.map((r, i) => {
    const on  = isSelected(r.id);
    const act = r.id === activeId;
    return (
      <Box key={r.id} sx={{
        display: 'flex', alignItems: 'center',
        borderBottom: '1px solid var(--divider)',
        bgcolor: act ? 'var(--row-hover)' : on ? 'var(--row-selected)' : 'transparent',
        boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none',
      }}>
        <Box
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); toggleRow(r.id); }}
          sx={{
            width: 40, alignSelf: 'stretch', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', borderRight: '1px solid var(--divider)',
            '&:hover': { bgcolor: 'rgba(14,165,233,.06)' },
          }}>
          <Check on={on} />
        </Box>
        <Box
          onClick={() => { openDetail(r.id); if (isMobile) setMobileDetailOpen(true); }}
          sx={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center',
            gap: '9px', px: '10px', py: '9px', cursor: 'pointer',
            '&:hover': { bgcolor: 'var(--row-hover)' },
          }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}
              title={r.name}>{r.name}</Typography>
            <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', mt: '2px' }}>{r.meta}</Typography>
            <Box sx={{ height: 4, borderRadius: '3px', bgcolor: 'var(--glass-hover)', mt: '6px', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(r.value / maxValue) * 100}%`, bgcolor: chartColor(i), borderRadius: '3px' }} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0, color: 'var(--text-secondary)' }}>
            {r.value.toLocaleString('ru-RU')}
          </Typography>
        </Box>
      </Box>
    );
  });

  // ── Shared tabs + search + select-all ────────────────────────────────────────

  const tabsSx = (
    <Box sx={{ display: 'flex', gap: '2px', p: '2px', borderRadius: '9px', ...glassSx }}>
      {(['players', 'files'] as Mode[]).map(m => (
        <Box key={m} component="button" onClick={() => { setMode(m); setSearch(''); }}
          sx={{
            flex: 1, py: '5px', borderRadius: '7px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit', border: '1px solid transparent',
            bgcolor: mode === m ? 'var(--selected-bg)' : 'transparent',
            borderColor: mode === m ? 'var(--selected-border)' : 'transparent',
            color: mode === m ? 'var(--text-primary)' : 'var(--text-disabled)',
          }}>
          {m === 'players' ? t('reports.tabPlayers') : t('reports.tabContent')}
        </Box>
      ))}
    </Box>
  );

  const searchSx = (
    <Box component="input"
      value={search}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      placeholder={mode === 'players' ? t('reports.searchPlayers') : t('reports.searchContent')}
      sx={{ width: '100%', bgcolor: 'var(--input-bg)', border: '1px solid var(--input-border)',
        borderRadius: '8px', px: '10px', py: '6px', fontSize: 13, color: 'var(--text-primary)',
        outline: 'none', fontFamily: 'inherit', '&:focus': { borderColor: 'var(--accent)' } }} />
  );

  const selectAllSx = (
    <Box onClick={toggleAll}
      sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '14px', py: '7px', cursor: 'pointer',
        borderBottom: '1px solid var(--divider)', bgcolor: 'var(--glass-hover)', flexShrink: 0 }}>
      <Check on={rows.length > 0 && selectedCount === rows.length} />
      <Typography sx={{ flex: 1, fontSize: 11, color: 'var(--text-secondary)' }}>
        {t('reports.selectedOf', { n: selectedCount, total: rows.length })}
      </Typography>
      <Typography sx={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-secondary)' }}>
        {selectedSum.toLocaleString('ru-RU')}
      </Typography>
    </Box>
  );

  // ── Aggregate body (reused in desktop right panel + mobile sheet) ──────────

  const aggregateBody = !d ? (
    <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)', py: '40px', textAlign: 'center' }}>
      {loading ? t('common.loading') : t('reports.noData')}
    </Typography>
  ) : (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
        <Kpi label={t('reports.kpiPlays')}   value={d.summary.total_plays.toLocaleString('ru-RU')} />
        <Kpi label={t('reports.kpiAirtime')} value={fmtHours(airtimeMs)} color="#22d3ee" />
        <Kpi label={t('reports.kpiPlayers')} value={String(d.summary.players_with_plays)} color="#34d399" />
        <Kpi label={t('reports.kpiErrors')}  value={String(d.summary.total_errors)} color="#fbbf24" />
      </Box>
      <Box>
        <SectionLabel text={t('reports.byDays')} />
        <BarChart data={d.series} />
      </Box>
      <Box>
        <SectionLabel text={t('reports.byHours')} />
        <HourChart hours={d.by_hour} />
      </Box>
      <Box sx={{ ...glassSx, borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{ px: '14px', py: '8px', borderBottom: '1px solid var(--divider)' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{t('reports.byPlayers')}</Typography>
        </Box>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t('reports.colPlayer'), t('reports.colStatus'), t('reports.colPlays'), t('reports.colFirst'), t('reports.colLast')].map((h, i) => (
                <th key={h} style={{
                  padding: '7px 12px', textAlign: i >= 2 ? 'right' : 'left', fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-disabled)',
                  borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.by_player.map(p => (
              <tr key={p.device_id}>
                <td style={tdSx}>{p.name || p.device_id}</td>
                <td style={tdSx}><StatusBadge online={isOnline(p.last_seen_at)} /></td>
                <td style={{ ...tdSx, textAlign: 'right', fontWeight: 700 }}>{p.plays.toLocaleString('ru-RU')}</td>
                <td style={{ ...tdSx, textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>{fmtDate(p.first_at)}</td>
                <td style={{ ...tdSx, textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>{fmtDate(p.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', lineHeight: 1.5 }}>
        {t('reports.airtimeNote')}
      </Typography>
    </>
  );

  // ── Single-item detail body (reused in desktop right panel + mobile sheet) ─

  const singleDetailBody = activeLoading || !activeDetail ? (
    <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)', py: '40px', textAlign: 'center' }}>
      {t('common.loading')}
    </Typography>
  ) : mode === 'players' ? (
    <PlayerDetail
      player={catalog?.by_player.find(p => p.device_id === activeId) ?? null}
      byFile={activeDetail.by_file}
      t={t}
    />
  ) : (
    <ContentDetail
      file={catalog?.by_file.find(f => String(f.file_id) === activeId) ?? null}
      byPlayer={activeDetail.by_player}
      t={t}
    />
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(var(--app-h) - 52px)', overflow: 'hidden', ...(!isMobile && { p: '16px', gap: '12px' }) }}>

      {/* ════════════════════ MOBILE LAYOUT ════════════════════ */}
      {isMobile && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>

          {/* Filter bar */}
          <Box sx={{ px: '12px', py: '10px', borderBottom: '1px solid var(--divider)', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            <CalendarPicker
              mode="range"
              startDate={range.from || null}
              endDate={range.to || null}
              onChange={(s, e) => {
                if (s && e) {
                  setPeriod('custom');
                  setRange({ from: moment(s).format('YYYY-MM-DD'), to: moment(e).format('YYYY-MM-DD') });
                }
              }}
              displayFormat="DD MMM"
              separator="—"
            />
            <Box sx={{ flex: 1 }} />
            <Box component="button" onClick={fetchCatalog} disabled={loading}
              sx={{ ...glassSx, borderRadius: '9px', px: '11px', py: '7px', fontSize: 13, fontFamily: 'inherit',
                color: 'var(--text-secondary)', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.5 : 1 }}>
              ↻
            </Box>
          </Box>

          {/* KPI strip */}
          {d && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px', px: '12px', py: '8px', flexShrink: 0 }}>
              <MobileKpi label={t('reports.kpiPlays')}   value={d.summary.total_plays.toLocaleString('ru-RU')} color="var(--accent-light)" />
              <MobileKpi label={t('reports.kpiAirtime')} value={fmtHours(airtimeMs)} color="#22d3ee" />
              <MobileKpi label={t('reports.kpiPlayers')} value={String(d.summary.players_with_plays)} color="#34d399" />
              <MobileKpi label={t('reports.kpiErrors')}  value={String(d.summary.total_errors)} color="#fbbf24" />
            </Box>
          )}

          {/* Mode tabs */}
          <Box sx={{ px: '12px', pb: '8px', flexShrink: 0 }}>{tabsSx}</Box>

          {/* Search */}
          <Box sx={{ px: '12px', pb: '8px', flexShrink: 0 }}>{searchSx}</Box>

          {/* Select-all */}
          {selectAllSx}

          {/* List */}
          <Box sx={{
            flex: 1, overflowY: 'auto',
            pb: selectedCount >= 2 ? '84px' : 0,
            transition: 'padding-bottom .3s',
            scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.13) transparent',
            '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.13)', borderRadius: 3 },
          }}>
            {rows.length === 0 ? (
              <Box sx={{ py: '40px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>
                  {loading ? t('common.loading') : t('reports.noData')}
                </Typography>
              </Box>
            ) : listRows}
          </Box>

          {/* Floating "View Report" button — visible when 2+ selected */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            px: '16px', pb: '20px', pt: '12px',
            background: 'linear-gradient(to top, var(--sidebar-bg, #0f1521) 55%, transparent)',
            zIndex: 40, pointerEvents: selectedCount >= 2 ? 'auto' : 'none',
            transform: selectedCount >= 2 ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform .3s cubic-bezier(.32,.72,0,1)',
          }}>
            <Box
              onClick={() => setMobileAggOpen(true)}
              sx={{
                py: '14px', borderRadius: '14px', textAlign: 'center', cursor: 'pointer',
                background: 'linear-gradient(135deg, var(--accent), var(--accent-light))',
                color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '-0.2px',
                boxShadow: '0 4px 24px var(--accent-glow)',
              }}
            >
              {t('reports.viewReportFor', { n: selectedCount })} {selectedCount === 1 ? t('reports.playerSingular') : (mode === 'players' ? t('reports.playersWord') : t('reports.filesWord'))}
            </Box>
          </Box>

          {/* ── Single-player bottom sheet ── */}
          <Drawer
            anchor="bottom"
            open={mobileDetailOpen}
            onClose={() => setMobileDetailOpen(false)}
            slotProps={{ paper: { sx: {
              borderRadius: '20px 20px 0 0',
              background: 'var(--glass-bg)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              height: '96dvh',
              display: 'flex', flexDirection: 'column',
              border: '1px solid var(--glass-border)', borderBottom: 'none',
              overflow: 'hidden',
            }}}}
          >
            {/* Handle */}
            <Box sx={{ width: 36, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '2px', mx: 'auto', mt: '10px', mb: 0, flexShrink: 0 }} />
            {/* Header */}
            <Box sx={{ px: '18px', py: '12px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {rows.find(r => r.id === activeId)?.name ?? activeId ?? ''}
              </Typography>
              <Box sx={{ px: '8px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(14,165,233,.12)', color: 'var(--accent-light)', flexShrink: 0 }}>
                ● {t('reports.badgeDetail')}
              </Box>
              <Box onClick={() => setMobileDetailOpen(false)}
                sx={{ width: 28, height: 28, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-disabled)', '&:hover': { bgcolor: 'var(--glass-hover)' } }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </Box>
            </Box>
            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '14px',
              scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.13) transparent' }}>
              {singleDetailBody}
            </Box>
          </Drawer>

          {/* ── Aggregate bottom sheet (multi-select report) ── */}
          <Drawer
            anchor="bottom"
            open={mobileAggOpen}
            onClose={() => setMobileAggOpen(false)}
            slotProps={{ paper: { sx: {
              borderRadius: '20px 20px 0 0',
              background: 'var(--glass-bg)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              height: '92vh',
              display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(99,102,241,0.3)', borderBottom: 'none',
              overflow: 'hidden',
            }}}}
          >
            <Box sx={{ width: 36, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '2px', mx: 'auto', mt: '10px', flexShrink: 0 }} />
            <Box sx={{ px: '18px', py: '12px', borderBottom: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>
                {t('reports.reportTitle')} · {selectedCount} {mode === 'players' ? t('reports.playersWord') : t('reports.filesWord')}
              </Typography>
              <Box sx={{ px: '8px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(99,102,241,0.15)', color: '#a5b4fc', flexShrink: 0 }}>
                ● Aggregate
              </Box>
              <Box onClick={() => setMobileAggOpen(false)}
                sx={{ width: 28, height: 28, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-disabled)', '&:hover': { bgcolor: 'var(--glass-hover)' } }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '14px',
              scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.13) transparent' }}>
              {aggregateBody}
            </Box>
          </Drawer>

        </Box>
      )}

      {/* ════════════════════ DESKTOP LAYOUT ════════════════════ */}
      {!isMobile && (
        <>
          {/* Filter bar */}
          <Box sx={{ ...cardSx, flexShrink: 0, px: '18px', py: '11px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <CalendarPicker
              mode="range"
              startDate={range.from || null}
              endDate={range.to || null}
              onChange={(s, e) => {
                if (s && e) {
                  setPeriod('custom');
                  setRange({ from: moment(s).format('YYYY-MM-DD'), to: moment(e).format('YYYY-MM-DD') });
                }
              }}
              displayFormat="DD MMM YYYY"
              separator="—"
            />
            <Box sx={{ flex: 1 }} />
            {d && (
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                {d.summary.total_plays.toLocaleString('ru-RU')} {t('reports.playsWord')} · {d.summary.players_with_plays} {t('reports.playersWord')}
              </Typography>
            )}
            <Box component="button" onClick={fetchCatalog} disabled={loading}
              sx={{ ...glassSx, borderRadius: '9px', px: '12px', py: '6px', fontSize: 12, fontWeight: 600,
                fontFamily: 'inherit', color: 'var(--text-secondary)', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.5 : 1 }}>
              ↻ {t('reports.refresh')}
            </Box>
          </Box>

          {/* Two islands */}
          <Box sx={{ flex: 1, display: 'flex', gap: '12px', overflow: 'hidden' }}>

            {/* LEFT: list */}
            <Box sx={{ ...cardSx, width: 330, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ p: '8px 12px', borderBottom: '1px solid var(--divider)' }}>{searchSx}</Box>
              <Box sx={{ p: '8px 12px', borderBottom: '1px solid var(--divider)' }}>{tabsSx}</Box>
              {selectAllSx}
              <Box sx={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.13) transparent',
                '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.13)', borderRadius: 3 } }}>
                {rows.length === 0 ? (
                  <Box sx={{ py: '40px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>
                      {loading ? t('common.loading') : t('reports.noData')}
                    </Typography>
                  </Box>
                ) : listRows}
              </Box>
              <Box sx={{ px: '16px', py: '8px', borderTop: '1px solid var(--divider)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                  {t('reports.selectedFooter', { n: selectedCount, total: rows.length })}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                  {selectedSum.toLocaleString('ru-RU')}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT: detail / aggregate */}
            <Box sx={{ ...cardSx, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
              <Box sx={{ px: '18px', py: '9px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {rightTitle}
                </Typography>
                {activeId && (
                  <Box sx={{ px: '8px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 700,
                    bgcolor: 'rgba(14,165,233,.12)', color: 'var(--accent-light)', flexShrink: 0 }}>
                    ● {t('reports.badgeDetail')}
                  </Box>
                )}
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace', flexShrink: 0 }}>
                  {range.from} — {range.to}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: '14px 18px', display: 'flex', flexDirection: 'column', gap: '14px',
                scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.13) transparent',
                '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.13)', borderRadius: 3 } }}>
                {activeId ? singleDetailBody : aggregateBody}
              </Box>
            </Box>

          </Box>
        </>
      )}

    </Box>
  );
};

// ── Player detail view ─────────────────────────────────────────────────────────

interface PlayerDetailProps {
  player: PlayerRow | null;
  byFile: FileRow[];
  t: (k: string, opts?: Record<string, unknown>) => string;
}

const PlayerDetail: React.FC<PlayerDetailProps> = ({ player, byFile, t }) => {
  const maxPlays = Math.max(...byFile.map(f => f.plays), 1);
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
        <Kpi label={t('reports.kpiPlays')}   value={player ? player.plays.toLocaleString('ru-RU') : '—'} />
        <Kpi label={t('reports.kpiContent')} value={String(byFile.length)} color="#34d399" />
        <Kpi label={t('reports.kpiAirtime')} value={player ? fmtHours(Number(player.duration_ms)) : '—'} color="#22d3ee" />
        <Kpi label={t('reports.kpiStatus')}  color={player && isOnline(player.last_seen_at) ? '#34d399' : '#64748b'}
          value={player ? (isOnline(player.last_seen_at) ? 'online' : 'offline') : '—'} />
      </Box>

      <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
        <Box sx={{ px: '14px', py: '8px', borderBottom: '1px solid var(--divider)' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{t('reports.videosByPlayer')}</Typography>
        </Box>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thSx}>#</th>
              <th style={thSx}>{t('reports.colClip')}</th>
              <th style={{ ...thSx, textAlign: 'center' }}>{t('reports.colDuration')}</th>
              <th style={{ ...thSx, textAlign: 'right' }}>{t('reports.colPlays')}</th>
              <th style={{ ...thSx, width: 120 }}>{t('reports.colShare')}</th>
            </tr>
          </thead>
          <tbody>
            {byFile.map((f, i) => (
              <tr key={f.file_id}>
                <td style={{ ...tdSx, color: 'var(--text-disabled)', width: 28 }}>{i + 1}</td>
                <td style={tdSx}>{baseName(f.name)}</td>
                <td style={{ ...tdSx, textAlign: 'center', fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>{fmtDur(f.duration_ms)}</td>
                <td style={{ ...tdSx, textAlign: 'right', fontWeight: 700 }}>{f.plays.toLocaleString('ru-RU')}</td>
                <td style={{ ...tdSx, paddingRight: 14 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'var(--glass-hover)', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${Math.round(f.plays / maxPlays * 100)}%`, bgcolor: 'var(--accent)', borderRadius: 2 }} />
                    </Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', width: 30, textAlign: 'right' }}>
                      {Math.round(f.plays / (byFile.reduce((s, x) => s + x.plays, 1)) * 100)}%
                    </Typography>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', lineHeight: 1.5 }}>
        {t('reports.airtimeNote')}
      </Typography>
    </>
  );
};

// ── Content detail view ────────────────────────────────────────────────────────

interface ContentDetailProps {
  file: FileRow | null;
  byPlayer: PlayerRow[];
  t: (k: string, opts?: Record<string, unknown>) => string;
}

const ContentDetail: React.FC<ContentDetailProps> = ({ file, byPlayer, t }) => {
  const maxPlays = Math.max(...byPlayer.map(p => p.plays), 1);
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
        <Kpi label={t('reports.kpiPlays')}   value={file ? file.plays.toLocaleString('ru-RU') : '—'} />
        <Kpi label={t('reports.kpiPlayers')} value={String(byPlayer.length)} color="#34d399" />
        <Kpi label={t('reports.kpiAirtime')} value={file ? fmtHours(Number(file.duration_ms)) : '—'} color="#22d3ee" />
        <Kpi label={t('reports.kpiErrors')}  value={file ? String(file.plays_error) : '—'} color="#fbbf24" />
      </Box>

      <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
        <Box sx={{ px: '14px', py: '8px', borderBottom: '1px solid var(--divider)' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{t('reports.playersForContent')}</Typography>
        </Box>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thSx}>{t('reports.colPlayer')}</th>
              <th style={thSx}>{t('reports.colStatus')}</th>
              <th style={{ ...thSx, textAlign: 'right' }}>{t('reports.colPlays')}</th>
              <th style={{ ...thSx, width: 140 }}>{t('reports.colShare')}</th>
            </tr>
          </thead>
          <tbody>
            {byPlayer.map(p => (
              <tr key={p.device_id}>
                <td style={tdSx}>{p.name || p.device_id}</td>
                <td style={tdSx}><StatusBadge online={isOnline(p.last_seen_at)} /></td>
                <td style={{ ...tdSx, textAlign: 'right', fontWeight: 700 }}>{p.plays.toLocaleString('ru-RU')}</td>
                <td style={{ ...tdSx, paddingRight: 14 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'var(--glass-hover)', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${Math.round(p.plays / maxPlays * 100)}%`, bgcolor: 'var(--accent)', borderRadius: 2 }} />
                    </Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', width: 30, textAlign: 'right' }}>
                      {Math.round(p.plays / (byPlayer.reduce((s, x) => s + x.plays, 1)) * 100)}%
                    </Typography>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', lineHeight: 1.5 }}>
        {t('reports.airtimeNote')}
      </Typography>
    </>
  );
};

// ── Small helpers ──────────────────────────────────────────────────────────────

const tdSx: React.CSSProperties = {
  padding: '8px 12px', borderBottom: '1px solid var(--divider)',
  fontSize: 12, color: 'var(--text-primary)',
};

const thSx: React.CSSProperties = {
  padding: '7px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-disabled)',
  borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap',
};

const Check: React.FC<{ on: boolean }> = ({ on }) => (
  <Box sx={{
    width: 15, height: 15, borderRadius: '5px', flexShrink: 0, fontSize: 10, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${on ? 'var(--accent)' : 'var(--input-border)'}`,
    bgcolor: on ? 'var(--accent)' : 'transparent',
  }}>{on ? '✓' : ''}</Box>
);

const StatusBadge: React.FC<{ online: boolean }> = ({ online }) => (
  <Box sx={{
    display: 'inline-flex', px: '7px', py: '1px', borderRadius: '20px', fontSize: 10,
    fontWeight: 700, textTransform: 'uppercase',
    bgcolor: online ? 'rgba(52,211,153,.12)' : 'rgba(148,163,184,.1)',
    color: online ? '#34d399' : '#94a3b8',
  }}>{online ? 'online' : 'offline'}</Box>
);

const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <Typography sx={{
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
    color: 'var(--text-disabled)', mb: '6px',
  }}>{text}</Typography>
);

const Kpi: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ ...glassSx, borderRadius: '12px', p: '12px 14px' }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '4px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: color || 'var(--accent-light)' }}>
      {value}
    </Typography>
  </Box>
);

const MobileKpi: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ ...glassSx, borderRadius: '10px', p: '8px 10px', textAlign: 'center' }}>
    <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1, mb: '3px', fontVariantNumeric: 'tabular-nums', color: color || 'var(--accent-light)' }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-disabled)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {label}
    </Typography>
  </Box>
);

export default ReportPage;
